import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { ConfirmationPopupComponent } from '../../components/confirmation-popup/confirmation-popup.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { ButtonComponent } from "../../components/button/button.component";
import { HeaderComponent } from "../../components/header/header.component";
import { TextInputComponent } from "../../components/text-input/text-input.component";
import { PluralPipe } from '../../pipes/plural.pipe';
import { DistrictService } from '../../services/district.service';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { CommonModule } from '@angular/common';
import { DropdownService } from '../../services/dropdown.service';
import { BulkUploadComponent } from '../../components/bulk-upload/bulk-upload.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { FilterByKeyAndValuePipe } from '../../pipes/filter-by-key-and-value.pipe';
import { ValidationService } from '../../services/validation.service';
import { ExamService } from '../../services/exam.service';
import { ToggleTabsComponent } from '../../components/toggle-tabs/toggle-tabs.component';
import { sub } from 'date-fns';
import { FormService } from '../../services/form.service';
import { ConfirmationdialogcomponentComponent } from '../../components/confirmationdialogcomponent/confirmationdialogcomponent.component';

@Component({
  selector: 'app-exam-result-list',
  imports: [HeaderComponent, ButtonComponent, CommonModule, ConfirmationPopupComponent, ModalComponent, ReactiveFormsModule, FormsModule, PluralPipe, SelectInputComponent, AgGridListComponent, BulkUploadComponent, SearchInputComponent, ToggleTabsComponent, ConfirmationdialogcomponentComponent],
  templateUrl: './exam-result-list.component.html',
  styleUrl: './exam-result-list.component.css'
})
export class ExamResultListComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('confirmation_delete_modal') confirmation_delete_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('confirmation_disabled_modal') confirmation_disabled_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('confirmation_publish_modal') confirmation_publish_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;
  @ViewChild('publish_modal') publish_modal: ConfirmationdialogcomponentComponent | undefined;

  @ViewChild('bulk_modal') bulk_modal: any
  page_name = 'Exam Result';
  form: FormGroup;
  params: any = {};
  selected_ids: any = {}
  items: any = [];
  is_loading = false;
  max_level: number = 0;

  // Filter bar dropdown options (derived from assessment_list)
  university_options: any[] = [];
  academic_year_options: any[] = [];
  exam_options: any[] = [];
  assessment_options: any[] = [];

  // Modal dropdown options (derived from assessment_list, tracked separately)
  modal_academic_year_options: any[] = [];
  modal_exam_options: any[] = [];
  modal_assessment_options: any[] = [];

  unreleased_columns: any = [
    { field: 'subject.name', headerName: 'Subject Name', filter: true, flex: 1.5 },
    { field: 'script_barcode', headerName: 'Roll No.', filter: true, flex: 1 },
    { field: 'script.script_id', headerName: 'Script ID', filter: true, flex: 1 },
    { field: 'total_marks', headerName: 'Total Marks', filter: true, flex: 1 },
    // { field: 'is_active', headerName: 'Status', filter: true, flex: 1, valueGetter: (params: any) => params.data?.is_active, cellRenderer: (params: any) => { return params.value ? 'Enable' : 'Disable'; } },
    // { headerName: "", sortable: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellRendererParams: { actions: this.actions.bind(this) }, editable: false, pinned: 'right', width: 130, minWidth: 130, maxWidth: 130 }
  ];

  released_columns: any = [
    { headerName: 'Subject Name (Code)', filter: true, flex: 1.5, valueGetter: (p: any) => { const s = p.data?.subject; return s ? `${s.name}${s.code ? ' (' + s.code + ')' : ''}` : '-'; } },
    { field: 'result_level', headerName: 'Published Level', filter: true, flex: 1, valueGetter: (p: any) => this.published_level_label(p.data?.result_level) },
    {
      headerName: 'Published By', filter: false, flex: 2,
      cellRenderer: (p: any) => {
        const name = p.data?.evaluator?.name || '';
        const email = p.data?.evaluator?.email || '';
        if (!name && !email) return '-';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;line-height:1.3"><span style="font-weight:600;font-size:13px">${name}</span><span style="font-size:12px;color:#6b7280">${email}</span></div>`;
      }
    },
    { headerName: 'Published Date & Time', filter: true, flex: 2, valueGetter: (p: any) => [p.data?.released_date, p.data?.released_time].filter(Boolean).join(' ') },
    {
      headerName: 'Action', sortable: false, filter: false, headerClass: 'custom-header-center',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: () => `<div style="display:flex;align-items:center;justify-content:center;height:100%"><button style="background:#EAEBFF;border:none;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer"><i class="bx bx-download" style="color:#3F41D1;font-size:18px"></i></button></div>`,
      onCellClicked: (p: any) => { if (p.data) this.download_result(p.data); },
      pinned: 'right', width: 80, minWidth: 80, maxWidth: 80
    }
  ];

  student_released_columns: any = [
    { headerName: 'Subject Name (Code)', filter: true, flex: 1.5, valueGetter: (p: any) => { const s = p.data?.subject; return s ? `${s.name}${s.code ? ' (' + s.code + ')' : ''}` : '-'; } },
    { field: 'roll_number', headerName: 'Roll No.', filter: true, flex: 1 },
    { field: 'script_id', headerName: 'Script ID', filter: true, flex: 1.5 },
    { field: 'total_marks', headerName: 'Total Marks', filter: true, flex: 1 },
    { field: 'passing_marks', headerName: 'Passing Marks', filter: true, flex: 1 },
    { field: 'obtained_marks', headerName: 'Obtained Marks', filter: true, flex: 1 },
    { headerName: 'Result', filter: true, flex: 1, valueGetter: (p: any) => { const ob = p.data?.obtained_marks; const pass = p.data?.passing_marks; if (ob == null) return '-'; return Number(ob) >= Number(pass) ? 'Pass' : 'Fail'; } },
    { headerName: 'Published Date & Time', filter: true, flex: 2, valueGetter: (p: any) => [p.data?.released_date, p.data?.released_time].filter(Boolean).join(' ') },
  ];

  columns: any = [...this.unreleased_columns];

  unreleased_bulk_columns: any[] = [
    { column_name: 'Roll No.', type: 'text', return_as: 'script_barcode', sample_value: 'M1' },
    { column_name: 'Script ID', type: 'text', return_as: 'script_id', sample_value: 'AE001123' },
    { column_name: 'Total Marks', type: 'number', return_as: 'total_marks', sample_value: '100' },
    { column_name: 'AI Score', type: 'number', return_as: 'marks.ai_score', sample_value: '23' },
    { column_name: 'L1 Marks', type: 'number', return_as: 'marks.l1_marks', sample_value: '23' },
    { column_name: 'L2 Marks', type: 'number', return_as: 'marks.l2_marks', sample_value: '22' },
    { column_name: 'L3 Marks', type: 'number', return_as: 'marks.l3_marks', sample_value: '23' },
    { column_name: 'L4 Marks', type: 'number', return_as: 'marks.l4_marks', sample_value: '11' },
    { column_name: 'HE Marks (if any)', type: 'number', return_as: 'marks.he_marks', sample_value: '12' },
  ];

  released_bulk_columns: any[] = [
    { column_name: 'Academic Year', type: 'text', return_as: 'academic_year.name', sample_value: '2025-26' },
    { column_name: 'Exam Name', type: 'text', return_as: 'exam.name', sample_value: 'SEM-1' },
    { column_name: 'Exam Code', type: 'text', return_as: 'exam.code', sample_value: '100' },
    { column_name: 'Subject Name', type: 'text', return_as: 'subject.name', sample_value: 'English' },
    { column_name: 'Subject Code', type: 'text', return_as: 'subject.code', sample_value: '1232' },
    { column_name: 'Released Level', type: 'text', return_as: 'result_level', sample_value: '1' },  // 1..N, or 'HE' / 'AI'
    { column_name: 'Released By', type: 'text', download_bind: ['evaluator.name', 'evaluator.email'], sample_value: 'John Doe' },
    { column_name: 'Release Date', type: 'text', return_as: 'released_date', sample_value: '2024-02-24' },
    { column_name: 'Release Time', type: 'text', return_as: 'released_time', sample_value: '10:00 AM' },
  ];

  bulk_columns: any[] = [...this.unreleased_bulk_columns];

  // `result_level` mirrors the published level: a number for evaluator levels, or the
  // literal 'HE' / 'AI' for a head-evaluation or AI Only publish.
  published_level_label(result_level: any) {
    if (result_level === null || result_level === undefined || result_level === '') return '-';
    const level = String(result_level).toUpperCase();
    if (level === 'AI') return 'AI Score';
    if (level === 'HE') return 'HE';
    return `Level ${result_level}`;
  }

  actions(row: any) {
    let actions: any = []
    actions.push({ text: 'Download', shortcut: 'u', color: 'sky', icon: 'bx bx-arrow-to-bottom', function: this.download_result.bind(this) })
    return actions
  }

  async download_result(item: any) {
    try {
      // Forward the current tab's result_type ('evaluation' | 're-evaluation') so
      // the BE filters/picks rows from the correct source. Without this hint the
      // BE falls back to a mixed precedence rule that surfaces evaluation marks
      // for non-re-evaluated scripts even from the Re-Evaluation Results tab.
      const result_type = this.params?.result_type === 're-evaluation' ? 're-evaluation' : undefined;
      const res = await this.ds.download_result({ assessment_id: item.assessment_id, ...(result_type && { result_type }) });
      const rows = res.data || [];
      if (!rows.length) {
        this.gs.toastr_shows_function('No data to download', 'Info', 'info');
        return;
      }

      const headers = ['Subject Name', 'Published Level', 'Roll No', 'Script ID', 'Total Marks', 'Obtained Marks', 'Result'];
      let csvContent = headers.join(',') + '\n';
      for (const row of rows) {
        csvContent += [row.subject_name, row.published_level, row.roll_no, row.script_id, row.total_marks, row.obtained_marks, row.result].join(',') + '\n';
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const subject_for_name = item.subject?.name || item.Subject?.name || item.assessment_id;
      const file_prefix = result_type === 're-evaluation' ? 'Re-Evaluation_Result' : 'Result';
      link.setAttribute('download', `${file_prefix}_${subject_for_name}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message




        || 'Download failed', 'Error', 'error');
    }
  }

  bulk_download_results() {
    const filter: any = {
      assessment_id: this.params.assessment_id,
      release_status: this.params.release_status,
    };
    if (this.params.result_type) filter.result_type = this.params.result_type;
    const file_name = this.params.result_type === 're-evaluation' ? 'Re-Evaluation result' : 'Exam result';
    this.fs.bulk_download(this.ds, 'get_exam_result_list', this.bulk_columns, file_name, filter);
  }

  published_count: number = 0;

  tabList: { name: string, label?: string, action: () => void }[] = [
    {
      name: 'unreleased', label: 'All', action: () => {
        this.params = { ...this.params, release_status: 'unreleased' };
        this.set_columns_by_status('unreleased');
        this.bulk_columns = [...this.unreleased_bulk_columns];
        this.route.navigate([], { relativeTo: this.ar, queryParams: { ...this.params } });
        this.get_list(this.params);
      }
    },
    {
      name: 'released', label: 'Published', action: () => {
        this.params = { ...this.params, release_status: 'released' };
        this.set_columns_by_status('released');
        this.bulk_columns = [...this.released_bulk_columns];
        this.route.navigate([], { relativeTo: this.ar, queryParams: { ...this.params } });
        this.get_list(this.params);
      }
    },
  ];

  selected_subjects: any[] = [];
  subject_levels_options: any[] = [];
  confirm_popup_title: string = 'Are you sure you want to release the result?';
  modalDropdownRegistry: { [depended: number]: any } = {};

  constructor(public gs: GlobalService, public route: Router, public fb: FormBuilder, public ds: ExamService, public ar: ActivatedRoute, public dds: DropdownService, public vs: ValidationService, public fs: FormService) {
    this.form = this.fb.group({
      university_id: ['', [Validators.required]],
      academic_year_id: ['', [Validators.required]],
      exam_id: ['', [Validators.required]],
      subject_ids: [[], [Validators.required]],
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit() {
    await this.dds?.get_dropdown_list(['university', 'academic-year', 'exam', 'level', 'course', 'program', 'stream', 'college', 'assessment_data']);
    this.update_filtered_options();
    this.ar.queryParams.pipe(
      debounceTime(50),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      takeUntil(this.destroy$)
    ).subscribe(async params => {
      this.params = { ...params }
      // Access Level: lock the filters this user cannot change before the first fetch.
      this.gs.apply_access_scope_to_params(this.params);
      if (!this.params.release_status) {
        this.params.release_status = 'unreleased';
      }
      this.page_name = this.params.result_type === 're-evaluation' ? 'Re-evaluation Result' : 'Evaluation Result';
      this.update_filtered_options();

      // Auto-select only on unreleased tab — released tab shows all published results without forcing a selection
      if (!this.params.assessment_id && this.params.release_status !== 'released' && this.assessment_options.length > 0 && this.is_initial_load) {
        const all = this.dds.dropdown_list?.assessment_data_list || [];
        const latest_option = this.assessment_options[0];
        const latest = all.find((a: any) => (a.value ?? a.id) == latest_option.value);
        if (latest) {
          this.is_initial_load = false;
          this.params.university_id = latest.university_id;
          this.params.academic_year_id = latest.academic_year_id;
          this.params.exam_id = latest.exam_id;
          this.params.assessment_id = latest_option.value;
          if (this.params.result_type) { /* keep */ }
          this.route.navigate([], { relativeTo: this.ar, queryParams: { ...this.params }, replaceUrl: true });
          return;
        }
      }

      // ng-select needs a tick after options update to show the bound value
      setTimeout(() => {
        if (this.dropdownRegistry[1]) this.dropdownRegistry[1].writeValue(this.params.university_id);
        if (this.dropdownRegistry[2]) this.dropdownRegistry[2].writeValue(this.params.academic_year_id);
        if (this.dropdownRegistry[3]) this.dropdownRegistry[3].writeValue(this.params.exam_id);
        if (this.dropdownRegistry[4]) this.dropdownRegistry[4].writeValue(this.params.assessment_id);
      });

      await this.get_list(this.params)
    })
  }

  update_filtered_options() {
    const all: any[] = this.dds.dropdown_list?.assessment_data_list || [];

    // University — unique from all assessments
    const seenUni = new Set();
    this.university_options = all.reduce((acc: any[], a: any) => {
      if (a.university_id && !seenUni.has(a.university_id)) {
        seenUni.add(a.university_id);
        acc.push({ title: a.University?.name || a.university?.name || String(a.university_id), value: a.university_id });
      }
      return acc;
    }, []);

    // Academic Year — filtered by selected university
    const byUni = this.params.university_id
      ? all.filter((a: any) => a.university_id == this.params.university_id)
      : all;
    const seenAY = new Set();
    this.academic_year_options = byUni.reduce((acc: any[], a: any) => {
      if (a.academic_year_id && !seenAY.has(a.academic_year_id)) {
        seenAY.add(a.academic_year_id);
        acc.push({ title: a.AcademicYear?.name || a.academic_year?.name || String(a.academic_year_id), value: a.academic_year_id });
      }
      return acc;
    }, []);

    // Exam — filtered by university + academic year
    const byAY = this.params.academic_year_id
      ? byUni.filter((a: any) => a.academic_year_id == this.params.academic_year_id)
      : byUni;
    const seenExam = new Set();
    this.exam_options = byAY.reduce((acc: any[], a: any) => {
      if (a.exam_id && !seenExam.has(a.exam_id)) {
        seenExam.add(a.exam_id);
        const examName = a.Exam?.name || a.exam?.name || String(a.exam_id);
        const examCode = a.Exam?.code || a.exam?.code || '';
        acc.push({ title: examCode ? `${examName} ( ${examCode} )` : examName, value: a.exam_id });
      }
      return acc;
    }, []);

    // Assessment/Subject — filtered by university + academic year + exam
    const byExam = this.params.exam_id
      ? byAY.filter((a: any) => a.exam_id == this.params.exam_id)
      : byAY;
    this.assessment_options = byExam.map((a: any) => {
      const subjectName = a.Subject?.name || a.subject?.name || a.title || String(a.id);
      const subjectCode = a.Subject?.code || a.subject?.code || '';
      return {
        title: subjectCode ? `${subjectName} ( ${subjectCode} )` : subjectName,
        value: a.id
      };
    });
  }

  update_modal_options() {
    const all: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    const uni = this.form.get('university_id')?.value;
    const ay = this.form.get('academic_year_id')?.value;
    const exam = this.form.get('exam_id')?.value;

    const byUni = uni ? all.filter((a: any) => a.university_id == uni) : all;
    const seenAY = new Set();
    this.modal_academic_year_options = byUni.reduce((acc: any[], a: any) => {
      if (a.academic_year_id && !seenAY.has(a.academic_year_id)) {
        seenAY.add(a.academic_year_id);
        acc.push({ title: a.AcademicYear?.name || a.academic_year?.name || String(a.academic_year_id), value: a.academic_year_id });
      }
      return acc;
    }, []);

    const byAY = ay ? byUni.filter((a: any) => a.academic_year_id == ay) : byUni;
    const seenExam = new Set();
    this.modal_exam_options = byAY.reduce((acc: any[], a: any) => {
      if (a.exam_id && !seenExam.has(a.exam_id)) {
        seenExam.add(a.exam_id);
        const examName = a.Exam?.name || a.exam?.name || String(a.exam_id);
        const examCode = a.Exam?.code || a.exam?.code || '';
        acc.push({ title: examCode ? `${examName} (${examCode})` : examName, value: a.exam_id });
      }
      return acc;
    }, []);

    const byExam = exam ? byAY.filter((a: any) => a.exam_id == exam) : byAY;
    this.modal_assessment_options = byExam.map((a: any) => {
      const subjectName = a.Subject?.name || a.subject?.name || a.title || String(a.id);
      const subjectCode = a.Subject?.code || a.subject?.code || '';
      return {
        title: subjectCode ? `${subjectName} (${subjectCode})` : subjectName,
        value: a.id
      };
    });
  }

  get_level_col() {
    if (!this.items?.data?.length) return;
    const marks = this.items.data[0].marks;
    if (!marks) return;

    this.max_level = 0;

    // Collect level keys and find max level
    const levelKeys: { key: string; num: number }[] = [];
    Object.keys(marks).forEach(key => {
      if (key.startsWith('l') && key.endsWith('_marks')) {
        const levelNum = parseInt(key.replace('l', '').replace('_marks', ''));
        if (!isNaN(levelNum)) {
          if (levelNum > this.max_level) this.max_level = levelNum;
          levelKeys.push({ key, num: levelNum });
        }
      }
    });
    levelKeys.sort((a, b) => a.num - b.num);

    // Build ordered list: AI Score → L1..LN → HE Marks
    const orderedKeys: { key: string; headerName: string }[] = [];
    if ('ai_score' in marks) orderedKeys.push({ key: 'ai_score', headerName: 'AI Score' });
    levelKeys.forEach(({ key, num }) => orderedKeys.push({ key, headerName: `L${num} Marks` }));
    if ('he_marks' in marks) orderedKeys.push({ key: 'he_marks', headerName: 'HE Marks' });

    orderedKeys.forEach(({ key, headerName }) => {
      const exists = this.columns.find((col: any) => col.field === `marks.${key}`);
      if (!exists) {
        this.columns.push({ field: `marks.${key}`, headerName, filter: true, flex: 1, cellDataType: false });
      }
    });

    // Explicitly update columns to trigger Ag-Grid refresh
    this.columns = [...this.columns];
    console.log("Max level detected:", this.max_level);

    // Sync bulk_columns with visible grid columns so Excel download matches the list
    this.bulk_columns = this.columns
      .filter((col: any) => col.field)
      .map((col: any) => ({
        column_name: col.headerName,
        type: col.field.startsWith('marks.') ? 'number' : 'text',
        return_as: col.field,
        sample_value: ''
      }));
  }

  set_columns_by_status(status: string) {
    if (status === 'released') {
      if (this.gs.user_data?.user_type === 'student') {
        this.columns = [...this.student_released_columns];
      } else {
        this.columns = [...this.released_columns];
        this.bulk_columns = [...this.released_bulk_columns];
      }
      return;
    }
    this.columns = [...this.unreleased_columns];
    this.bulk_columns = [...this.unreleased_bulk_columns];
  }

  async get_list(filter?: any) {
    try {
      this.is_loading = true;
      this.columns = [];
      if (!this.params.assessment_id && (this.params.release_status == 'unreleased' || !this.params.release_status)) {
        this.items = { data: [], count: 0 };
        const count_filter: any = { release_status: 'released', page_size: 1 };
        if (this.params.result_type) count_filter.result_type = this.params.result_type;
        const count_res = await this.ds.get_exam_result_list(count_filter);
        if (count_res?.published_count !== undefined) {
          this.published_count = count_res.published_count;
          this.tabList = this.tabList.map((tab: any) =>
            tab.name === 'released' ? { ...tab, label: `Published (${this.published_count})` } : tab
          );
        }
        return;
      }
      this.items = { data: [], count: 0 };
      const mergedFilter = { ...this.params, ...filter };
      this.params = { ...this.params, ...filter };
      const is_released = mergedFilter?.release_status === 'released';
      if (true) {
        const apiFilter = { ...mergedFilter };
        const res = await this.ds.get_exam_result_list(apiFilter);

        this.items = res || { data: [], count: 0 };
        if (res?.published_count !== undefined) {
          this.published_count = res.published_count;
          this.tabList = this.tabList.map(tab =>
            tab.name === 'released' ? { ...tab, label: `Published (${this.published_count})` } : tab
          );
        }
        if (this.items.data) {
          const page = mergedFilter?.page || 1;
          const pagesize = mergedFilter?.page_size || 10;
          this.items.data = this.items.data.map((item: any, index: number) => ({
            ...item,
            SrNo: (page - 1) * pagesize + index + 1
          }));
          if (is_released && this.gs.user_data?.user_type === 'student') {
            this.items.data = this.items.data.map((item: any) => {
              const script = item.scripts?.[0] || {};
              return { ...item, roll_number: script.roll_number, barcode: script.barcode, script_id: script.script_id, total_marks: script.total_marks, passing_marks: script.passing_marks, obtained_marks: script.obtained_marks };
            });
          }
        }
        if (this.items?.data?.length) {
          this.set_columns_by_status(is_released ? 'released' : (mergedFilter?.release_status || 'unreleased'));
          this.get_level_col();
        }

      }
    } catch (error: any) {
      console.error("Error fetching Result list:", error);
    } finally {
      this.is_loading = false;
    }
  }

  async open_delete_modal(item: any, index: any) {
    this.selected_ids = item;
    this.confirmation_delete_modal?.open();
  }

  async disable(item: any, index: any) {
    this.selected_ids = item;
    this.confirmation_disabled_modal?.open();
  }


  async delete() {
    try {
      let res = await this.ds.delete(this.selected_ids.id);
      this.gs.toastr_shows_function(res?.message




        , 'Success', 'success');
      this.selected_ids = {};
      this.confirmation_delete_modal?.close()
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message




        , 'Error', 'error');
    }
  }



  async bulk_delete(ids: any[]) {
    try {
      let res: any
      for (let id of ids) {
        res = await this.ds.delete(id);
      }
      this.gs.toastr_shows_function(res?.message, 'Success', 'success');
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  async open_form_modal(item?: any) {
    this.selected_ids = item || {};
    this.form.reset();
    this.selected_subjects = [];

    if (this.selected_ids && this.selected_ids.id) {
      let response = await this.ds.get(item.id);
      this.selected_ids = this.gs.addHierarchyIds({ ...response.data });
      this.form.patchValue({ ...this.selected_ids, });
    } else {
      this.selected_ids = {};
      this.form.reset();
    }
    this.form_modal?.open();
  }

  close_form_modal(): void {
    this.selected_subjects = [];
    this.subject_levels_options = [];
    this.form.reset();
    this.form_modal?.close();
  }

  async open_publish_confirmation() {
    if (this.form.invalid) {
      this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
      this.form.markAllAsTouched();
      return;
    }
    const subjectsWithoutLevel = this.selected_subjects.filter(s => !s.level);
    if (subjectsWithoutLevel.length > 0) {
      this.gs.toastr_shows_function('Please select level for all subjects.', 'Error', 'error');
      return;
    }
    this.publish_modal?.open();
  }

  async submit() {
    try {
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      this.publish_modal?.open()
      this.close_form_modal();
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message, 'Error', 'error');
    }
  }

  async publishResult() {
    try {
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      const subjectsWithoutLevel = this.selected_subjects.filter(s => !s.level);
      if (subjectsWithoutLevel.length > 0) {
        this.gs.toastr_shows_function('Please select level for all subjects.', 'Error', 'error');
        return;
      }
      const published_result = this.selected_subjects.map(subject => ({
        assessment_id: subject.id,
        level: String(subject.level)
      }));
      const source = this.params?.result_type === 're-evaluation' ? 're-evaluation' : 'evaluation';
      const res = await this.ds.release_result({ published_result, source });
      const failed = (res?.data || []).filter((r: any) => r.status === 'failed');
      if (failed.length) {
        failed.forEach((r: any) => this.gs.toastr_shows_function(r.message, 'Error', 'error'));
        return;
      }
      this.gs.toastr_shows_function('Results published successfully', 'Success', 'success');
      this.close_form_modal();
      const releasedTab = this.tabList.find(t => t.name === 'released');
      if (releasedTab) releasedTab.action();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Publish failed', 'Error', 'error');
    }
  }

  async bulk_upload(data: any, spinner: any) {
    try {
      const updatedData = data.map((item: any) => ({
        ...item,
        is_active: true
      }));
      await this.gs.bulk_form_submission(updatedData, this.ds, this.params, this.get_list.bind(this), this.bulk_modal, spinner)
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message, 'Error', 'error');

    }
  }

  is_initial_load = true;
  dropdownRegistry: { [depended: number]: any } = {};

  registerDropdown(event: { depended: number; ref: any }) {
    this.dropdownRegistry[event.depended] = event.ref;
  }

  async onDropdownChange(event: { value: any, depended: number }) {
    this.is_initial_load = false;
    console.log("Dropdown changed:", event);

    // Reset dependent dropdowns first
    const current = event.depended;
    for (const key of Object.keys(this.dropdownRegistry).map(Number)) {
      if (key > current) {
        this.dropdownRegistry[key]?.reset();
      }
    }

    if (event.depended == 1) {
      this.params.university_id = event.value ? event.value.value : null;
      this.params.academic_year_id = null;
      this.params.exam_id = null;
      this.params.assessment_id = null;
    }
    if (event.depended == 2) {
      this.params.academic_year_id = event.value ? event.value.value : null;
      this.params.exam_id = null;
      this.params.assessment_id = null;
    }
    if (event.depended == 3) {
      this.params.exam_id = event.value ? event.value.value : null;
      this.params.assessment_id = null;
    }
    if (event.depended == 4) {
      this.params.assessment_id = event.value ? event.value.value : null;
    }

    this.update_filtered_options();

    // Sync URL with all params - build clean query params object
    const queryParams: any = {
      release_status: this.params.release_status
    };

    if (this.params.university_id) queryParams.university_id = this.params.university_id;
    if (this.params.academic_year_id) queryParams.academic_year_id = this.params.academic_year_id;
    if (this.params.exam_id) queryParams.exam_id = this.params.exam_id;
    if (this.params.assessment_id) queryParams.assessment_id = this.params.assessment_id;
    // Preserve the evaluation/re-evaluation discriminator so dropdown filter changes
    // don't bounce the user back to the default (Evaluation) results view.
    if (this.params.result_type) queryParams.result_type = this.params.result_type;

    this.route.navigate([], {
      relativeTo: this.ar,
      queryParams: queryParams
    });
  }

  // Modal dropdown handling
  registerModalDropdown(event: { depended: number; ref: any }) {
    this.modalDropdownRegistry[event.depended] = event.ref;
  }

  async onModalDropdownChange(event: { value: any, depended: number }) {
    console.log("Modal Dropdown changed:", event);

    if (event.depended == 1) {
      this.form.patchValue({ academic_year_id: null, exam_id: null, subject_ids: [] }, { emitEvent: false });
      this.selected_subjects = [];
    }
    if (event.depended == 2) {
      this.form.patchValue({ exam_id: null, subject_ids: [] }, { emitEvent: false });
      this.selected_subjects = [];
    }
    if (event.depended == 3) {
      this.form.patchValue({ subject_ids: [] }, { emitEvent: false });
      this.selected_subjects = [];
    }

    const current = event.depended;
    for (const key of Object.keys(this.modalDropdownRegistry).map(Number)) {
      if (key > current) {
        this.modalDropdownRegistry[key]?.reset();
      }
    }

    this.update_modal_options();
  }

  onSubjectSelectionChange(event: any) {
    const selectedIds = this.form.get('subject_ids')?.value || [];
    const assessmentList = this.dds.dropdown_list?.assessment_data_list || [];

    this.selected_subjects = selectedIds.map((id: number) => {
      const assessment = assessmentList.find((item: any) => item.value == id);
      const existingSubject = this.selected_subjects.find((s: any) => s.id == id);

      // AI Only assessments have no evaluator levels at all, so "AI Score" is the only
      // thing that can be published for them. Any other assessment that ran AI evaluation
      // can be published at "AI Score" too — whichever level is picked, a script the head
      // evaluator actually checked publishes the HE marks (backend precedence).
      const is_ai_only = String(assessment?.no_of_evaluation).toUpperCase() === 'AI';
      const has_ai_evaluation = String(assessment?.ai_evaluation).toLowerCase() === 'enable';
      const levels_options: any[] = [];
      if (is_ai_only) {
        levels_options.push({ title: 'AI Score', value: 'AI', disabled: false });
      } else {
        const no_of_evaluation = Number(assessment?.no_of_evaluation) || this.max_level || 1;
        for (let i = 1; i <= no_of_evaluation; i++) {
          levels_options.push({ title: `Level ${i}`, value: String(i) });
        }
        levels_options.push({ title: 'AI Score', value: 'AI', disabled: !has_ai_evaluation });
      }

      // Keep the previous pick only if it is still a valid choice, otherwise pre-select
      // the single available option (the AI Only case).
      const previous_level = existingSubject?.level ?? null;
      const previous_still_valid = levels_options.some((o: any) => !o.disabled && o.value === previous_level);
      const level = previous_still_valid
        ? previous_level
        : (levels_options.length === 1 && !levels_options[0].disabled ? levels_options[0].value : null);

      return {
        id: id,
        name: assessment?.title || assessment?.Subject?.name || 'Unknown',
        level: level,
        is_ai_only: is_ai_only,
        assessment: assessment,
        levels_options: levels_options
      };
    });
  }

  removeSubject(subjectId: number) {
    this.selected_subjects = this.selected_subjects.filter(s => s.id !== subjectId);
    const currentIds = this.form.get('subject_ids')?.value || [];
    const newIds = currentIds.filter((id: number) => id !== subjectId);
    this.form.patchValue({ subject_ids: newIds });
  }
}
